import React from 'react'
import { useTranslation } from 'react-i18next'

import BoldSpan from '../../../components/display/BoldSpan'
import { FAQItem } from '../../../components/landing/FAQ'
import { Steps } from '../../../components/landing/IllustratedSteps'

import BuildYourEscape from './iconography/BuildYourEscape'
import GetInspired from './iconography/GetInspired'
import GetOutOfTown from './iconography/GetOutOfTown'
import ReflectAndReview from './iconography/ReflectAndReview'
import SaveYourFaves from './iconography/SaveYourFaves'

export const headerImage = {
  medium: {
    width: 620,
    url:
      'https://assets.riparide.com/landing_pages/stories/customer/header/medium-4a7ec8cf2f790f5beef669a471dcfc52.jpg',
  },
  large: {
    width: 1290,
    url:
      'https://assets.riparide.com/landing_pages/stories/customer/header/large-79ba0a55c081c68d49d27e36cd8bde26.jpg',
  },
  hero: {
    width: 1300,
    url:
      'https://assets.riparide.com/landing_pages/stories/customer/header/hero-87932f356713a3d071cc50701b08f7ee.jpg',
  },
}

const HeaderHeading = () => {
  const [t] = useTranslation('spelling')

  return <>Stories from real {t('traveler')}s</>
}

export const headerText = {
  heading: <HeaderHeading />,
  subheading:
    'Authentic travel experiences to inspire your next soul-fulfilling escape',
}

const TEMP_TEXT =
  'Cloud bread tote bag glossier, flannel synth narwhal hoodie pour-over adaptogen put a bird on it copper mug 3 wolf moon chia pok pok. Hot chicken sartorial slow-carb, ugh gluten-free hammock sriracha af.'

export const faqItems: FAQItem[] = [
  {
    title: 'How does Escape Builder work?',
    content: TEMP_TEXT,
  },
  {
    title: 'Who are Storytellers?',
    content: TEMP_TEXT,
  },
  {
    title: 'Can anyone share a story?',
    content: TEMP_TEXT,
  },
  {
    title: 'Can Riparide guarantee story content accuracy?',
    content: TEMP_TEXT,
  },
]

export const howItWorksSteps: Steps = [
  {
    title: 'Get inspired',
    explanation:
      'Deep dive into a world of unique stays and local adventures, and get inspired by other explorers’ soul-fulfilling travel experiences.',
    IllustrationComponent: GetInspired,
  },
  {
    title: 'Save your faves',
    explanation:
      'Found the perfect beach escape? Keen on a particular hike? You can save your faves to your Riparide Bucket List.',
    IllustrationComponent: SaveYourFaves,
  },
  {
    title: 'Build your escape',
    explanation:
      'Book and build your epic Riparide escape. You can piece together your dream Riparide, adventure by adventure.',
    IllustrationComponent: BuildYourEscape,
  },
  {
    title: 'Get out of town',
    explanation:
      'Break free, let loose and recharge. Your Riparide escape will get you out into nature and living fully.',
    IllustrationComponent: GetOutOfTown,
  },
  {
    title: 'Reflect and review',
    explanation:
      'At the end of your trip, did you feel peaceful? Rejuvenated? Challenged? We want to know. Review your escape and tell us about all those soul-fulfilling moments.',
    IllustrationComponent: ReflectAndReview,
  },
]

const BriefText = () => {
  const [t] = useTranslation('spelling')
  return (
    <>
      <BoldSpan>Riparide Stories</BoldSpan> are authentic experiences shared by
      real {t('traveler')}s (who are handy with a camera). It&apos;s like
      reading your best friend&apos;s travel journal – packed with photos,
      honest reviews and visual itineraries.
      <br />
      <br />
      Stories inspire people to disconnect from the grind and chase
      soul-fulfilling moments in nature.
    </>
  )
}

export const briefText = <BriefText />

export const benefitsText = [
  {
    heading: 'Unlock unique experiences',
    text:
      'From hidden waterfalls to wilderness cabins, Riparide Stories help you discover unique experiences in your own backyard.',
  },
  {
    heading: 'Explore your backyard',
    text:
      'Discover hidden local gems and unique experiences just a couple of hours drive from the city. Perfect for a weekend escape.',
  },
  {
    heading: 'Easy planning',
    text:
      'Where to go, what to do, where to stay – Riparide Stories make it easy to plan a soul-fulfilling escape.',
  },
  {
    heading: 'Connect with people',
    text:
      'These are first-hand stories from people just like you. Connect with their travel vibes, get inspired by their adventures and then get the keys to live out your own epic experience.',
  },
  {
    heading: 'Live Fully',
    text:
      'Riparide Stories inspire us all to take a break from the mundane and chase those soul-fulfilling moments. It’s why we’re in this business.',
  },
]

export const benefitsImages = [
  {
    medium:
      'https://assets.riparide.com/landing_pages/stories/customer/benefits/1/medium-cf06bdecf3640eee7dd172b14f3c7fa3.jpg',
    large:
      'https://assets.riparide.com/landing_pages/stories/customer/benefits/1/large-b3a9a3b1ab52f57dc9aba24eb77c7d0e.jpg',
  },
  {
    medium:
      'https://assets.riparide.com/landing_pages/stories/customer/benefits/2/medium-ff20387e0eed986fb012738076fbdf0b.jpg',
    large:
      'https://assets.riparide.com/landing_pages/stories/customer/benefits/2/large-9d472838580989850a91be74e412a104.jpg',
  },
  {
    medium:
      'https://assets.riparide.com/landing_pages/stories/customer/benefits/3/medium-3e1acd9634be3720010c89da8a4484de.jpg',
    large:
      'https://assets.riparide.com/landing_pages/stories/customer/benefits/3/large-5ffb89993bf09446e205ecca87015a7d.jpg',
  },
  {
    medium:
      'https://assets.riparide.com/landing_pages/stories/customer/benefits/4/medium-20ef4487b73ff9015230c9d6b7d0ad8a.jpg',
    large:
      'https://assets.riparide.com/landing_pages/stories/customer/benefits/4/large-522b1a344794cbf0e6f459f225f67b44.jpg',
  },
  {
    medium:
      'https://assets.riparide.com/landing_pages/stories/customer/benefits/5/medium-88e1bf03fd2e1b138fa638635d174139.jpg',
    large:
      'https://assets.riparide.com/landing_pages/stories/customer/benefits/5/large-bb9806ad324015d5df11e90862b0e8fd.jpg',
  },
  {
    medium:
      'https://assets.riparide.com/landing_pages/stories/customer/benefits/6/medium-6b6329af529753291f0f0a43a0504573.jpg',
    large:
      'https://assets.riparide.com/landing_pages/stories/customer/benefits/6/large-63c99f813ca7a1f7d754846b5d635ff6.jpg',
  },
]

export const benefitsPromoImage = {
  large: {
    url:
      'https://assets.riparide.com/landing_pages/stories/customer/benefits/large_promo/large-060df928d384f17681e57bd40bba6f36.jpg',
    width: 1300,
  },
  hero: {
    url:
      'https://assets.riparide.com/landing_pages/stories/customer/benefits/large_promo/hero-42ff68096ef3553d6650eb145a902c9c.jpg',
    width: 577,
  },
}

export const storiesForCarousel = [
  {
    id: '132',
    url: '/stories/132-sweet-tooth-satisfied',
    title: 'Sweet Tooth Satisfied',
    promoImage: {
      large:
        'https://assets.riparide.com/story/132/promo_image/large-567766988e4004e047ab6e7985f9d2ff.jpg',
    },
    user: {
      id: '8691',
      firstName: 'Trent ',
      avatar: {
        thumbLg:
          'https://assets.riparide.com/user/8691/avatar/thumb_lg-9a0f8dfb525d09968325bfe9c105a945.jpg',
      },
    },
  },
  {
    id: '78',
    url: '/stories/78-winter-in-wilsons',
    title: 'Winter In Wilsons',
    promoImage: {
      large:
        'https://assets.riparide.com/story/78/promo_image/large-48a9d04334e883976732b38f9e741e0c.jpg',
    },
    user: {
      id: '1443',
      firstName: 'Jesse',
      avatar: {
        thumbLg:
          'https://assets.riparide.com/user/1443/avatar/thumb_lg-e3333569e4f1e5538ae0bec319be8e57.jpg',
      },
    },
  },
  {
    id: '545',
    url: '/stories/545-historic-cottage-surrounded-by-natural-beauty',
    title: 'Historic Cottage Surrounded by Natural Beauty',
    promoImage: {
      large:
        'https://assets.riparide.com/story/545/promo_image/large-59a559688db23e7abbca4a8b509422fa.jpg',
    },
    user: {
      id: '37495',
      firstName: 'Tarynn',
      avatar: {
        thumbLg:
          'https://assets.riparide.com/user/37495/avatar/thumb_lg-723b7abfa6f238a613641c0384715541.jpg',
      },
    },
  },
  {
    id: '516',
    url: '/stories/516-luxury-farmstay',
    title: 'Luxury Farmstay ',
    promoImage: {
      large:
        'https://assets.riparide.com/story/516/promo_image/large-4ae62cfe434ad453944bec89f7f0bdcd.jpg',
    },
    user: {
      id: '19577',
      firstName: 'Ellie',
      avatar: {
        thumbLg:
          'https://assets.riparide.com/user/19577/avatar/thumb_lg-48c5d523f99ac15a11b8033a15a6dfa8.jpg',
      },
    },
  },
  {
    id: '142',
    url: '/stories/142-autumn-leaves',
    title: 'Autumn Leaves',
    promoImage: {
      large:
        'https://assets.riparide.com/story/142/promo_image/large-d53910bbc34260b4268558f3b1dd5f6f.jpg',
    },
    user: {
      id: '7212',
      firstName: 'Cameron',
      avatar: {
        thumbLg:
          'https://assets.riparide.com/user/7212/avatar/thumb_lg-a1831e2a921bc5f041be78084fdddd93.jpg',
      },
    },
  },
  {
    id: '645',
    url: '/stories/645-slow-living',
    title: 'Slow Living',
    promoImage: {
      large:
        'https://assets.riparide.com/story/645/promo_image/large-a4049cbd80b99f44ab792b543cb77168.jpg',
    },
    user: {
      id: '7856',
      firstName: 'Alexandra',
      avatar: {
        thumbLg:
          'https://assets.riparide.com/user/7856/avatar/thumb_lg-8f75cd65126d20a7aa7e8ef9ac87ebd8.jpg',
      },
    },
  },
  {
    id: '135',
    url: '/stories/135-cabin-by-the-sea',
    title: 'Cabin by the Sea',
    promoImage: {
      large:
        'https://assets.riparide.com/story/135/promo_image/large-775f25e8cef0970bc4b83d05f0ebab42.jpg',
    },
    user: {
      id: '9434',
      firstName: 'Melissa ',
      avatar: {
        thumbLg:
          'https://assets.riparide.com/user/9434/avatar/thumb_lg-69ad31249cc7a0ae16864e199cd01b04.png',
      },
    },
  },
  {
    id: '92',
    url: '/stories/92-barn-country',
    title: 'Barn Country',
    promoImage: {
      large:
        'https://assets.riparide.com/story/92/promo_image/large-453255e23f49e8c5f5669fcafc7c2e85.jpg',
    },
    user: {
      id: '4783',
      firstName: 'Brook & Brit',
      avatar: {
        thumbLg:
          'https://assets.riparide.com/user/4783/avatar/thumb_lg-cd0e680fa3b2638904d1211486530263.png',
      },
    },
  },
  {
    id: '663',
    url: '/stories/663-rhythm-of-nature',
    title: 'Rhythm of Nature',
    promoImage: {
      large:
        'https://assets.riparide.com/story/663/promo_image/large-fc68e62edfaa312a7c435a4708eb3f5a.jpg',
    },
    user: {
      id: '60700',
      firstName: 'Kadek',
      avatar: {
        thumbLg:
          'https://assets.riparide.com/user/60700/avatar/thumb_lg-56bf6534e0e2d8dcfa65dd4f3ed6b087.jpg',
      },
    },
  },
  {
    id: '647',
    url: '/stories/647-inverloch-glamping-retreat',
    title: 'Inverloch Glamping Retreat',
    promoImage: {
      large:
        'https://assets.riparide.com/story/647/promo_image/large-fd20899a26a722dfda80b89a96c1c10e.jpg',
    },
    user: {
      id: '35598',
      firstName: 'Jarryd ',
      avatar: {
        thumbLg:
          'https://assets.riparide.com/user/35598/avatar/thumb_lg-0303c1fb7f7e317391699e2fabd114a5.png',
      },
    },
  },
  {
    id: '540',
    url: '/stories/540-out-by-the-orchard',
    title: 'Out By The Orchard',
    promoImage: {
      large:
        'https://assets.riparide.com/story/540/promo_image/large-8e03ea0d5586a119d999241b78683260.jpg',
    },
    user: {
      id: '12682',
      firstName: 'Liam & Alexis',
      avatar: {
        thumbLg:
          'https://assets.riparide.com/user/12682/avatar/thumb_lg-2c55c56e222fe9ad0b0108ce36e1cdc5.jpg',
      },
    },
  },
  {
    id: '514',
    url: '/stories/514-treehouse-in-the-clouds',
    title: 'Treehouse in the Clouds',
    promoImage: {
      large:
        'https://assets.riparide.com/story/514/promo_image/large-92eea0cbb04fcd57879042a721b7105a.jpg',
    },
    user: {
      id: '21494',
      firstName: 'Shan',
      avatar: {
        thumbLg:
          'https://assets.riparide.com/user/21494/avatar/thumb_lg-1243b95af7364b0a954a916e480c42eb.png',
      },
    },
  },
]
